import { Ban, Check, Clock, Mail, RefreshCw, Save, Send, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { EmailStatusBadge, FilterChips, Pagination } from '../components/ui/data';
import { ConfirmDialog, Drawer, useToast } from '../components/ui/overlays';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  Select,
  Table,
  Td,
  Th,
  Textarea,
  Tr,
} from '../components/ui/primitives';
import {
  useBulkEmailAction,
  useDeleteEmail,
  useEmail,
  useEmailAction,
  useEmailStatusCounts,
  useEmails,
  useRunWorker,
  useSaveEmail,
  type EmailFilters,
} from '../hooks/queries';
import { formatDateTime, formatNumber, formatRelative, truncate } from '../lib/utils';

const STATUS_CHIPS = [
  { value: 'DRAFT', label: 'Drafts' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'SENT', label: 'Sent' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export const EmailsPage = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const [baseFilters, setFilters] = useState<EmailFilters>({
    page: 1,
    pageSize: 25,
    direction: 'OUTBOUND',
  });
  const filters = { ...baseFilters, status: searchParams.get('status') || undefined };
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading, error } = useEmails(projectId, filters);
  const { data: counts } = useEmailStatusCounts(projectId);
  const bulkAction = useBulkEmailAction(projectId);
  const runWorker = useRunWorker();

  const emails = data?.items ?? [];
  const allSelected = emails.length > 0 && emails.every((email) => selected.has(email.id));

  useEffect(() => setSelected(new Set()), [baseFilters.page, baseFilters.direction, searchParams]);
  useEffect(() => setFilters((current) => ({ ...current, page: 1 })), [searchParams]);

  const approveSelected = async () => {
    try {
      const result = await bulkAction.mutateAsync({
        emailIds: Array.from(selected),
        status: 'SCHEDULED',
      });
      toast.success(`Approved ${result.updated} email(s) — the send worker takes it from here`);
      setSelected(new Set());
    } catch (error) {
      toast.error(error);
    }
  };

  const cancelSelected = async () => {
    try {
      const result = await bulkAction.mutateAsync({
        emailIds: Array.from(selected),
        status: 'CANCELLED',
      });
      toast.success(`Cancelled ${result.updated} email(s)`);
      setSelected(new Set());
    } catch (error) {
      toast.error(error);
    }
  };

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Emails"
        description="Drafts written by agents, what is scheduled, and everything already sent."
        actions={
          <Button
            icon={<RefreshCw className="h-4 w-4" />}
            loading={runWorker.isPending}
            onClick={async () => {
              try {
                const result = (await runWorker.mutateAsync('send')) as {
                  sent: number;
                  skipped: number;
                };
                toast.success(
                  `Send worker ran: ${result.sent} sent, ${result.skipped} held back (window, limits or suppression).`,
                );
              } catch (error) {
                toast.error(error);
              }
            }}
          >
            Run send worker now
          </Button>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
          <FilterChips
            options={STATUS_CHIPS}
            value={filters.status}
            onChange={(status) => {
              const params = new URLSearchParams(searchParams);
              status ? params.set('status', status) : params.delete('status');
              setSearchParams(params, { replace: true });
              setFilters((current) => ({ ...current, page: 1 }));
              setSelected(new Set());
            }}
            counts={counts}
          />
          <div className="flex-1" />
          <Select
            aria-label="Email direction"
            className="w-auto"
            value={filters.direction ?? ''}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                direction: event.target.value || undefined,
                page: 1,
              }))
            }
          >
            <option value="OUTBOUND">Outbound</option>
            <option value="INBOUND">Inbound</option>
            <option value="">Both</option>
          </Select>
        </div>

        {selected.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-line bg-brand/5 px-4 py-2.5">
            <span className="text-[13px] font-medium">{selected.size} selected</span>
            <Button
              variant="primary"
              size="sm"
              icon={<Check className="h-3.5 w-3.5" />}
              onClick={approveSelected}
              loading={bulkAction.isPending}
            >
              Approve &amp; schedule
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={<Ban className="h-3.5 w-3.5" />}
              onClick={cancelSelected}
            >
              Cancel
            </Button>
            <div className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        ) : null}

        {error ? <div className="p-5"><ErrorBlock error={error} /></div> : emails.length === 0 ? (
          <EmptyState
            icon={<Mail className="h-5 w-5" />}
            title={filters.status ? 'No emails with this status' : 'No emails yet'}
            description={filters.status ? 'Choose another status or view all emails.' : 'Queue outreach from Leads to have your agent prepare emails for review.'}
            action={filters.status ? <Button onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.delete('status');
              setSearchParams(params, { replace: true });
            }}>View all emails</Button> : undefined}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th className="w-9">
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      className="h-3.5 w-3.5 cursor-pointer accent-[rgb(var(--brand))]"
                      checked={allSelected}
                      onChange={() =>
                        setSelected(allSelected ? new Set() : new Set(emails.map((email) => email.id)))
                      }
                    />
                  </Th>
                  <Th>Subject</Th>
                  <Th>Recipient</Th>
                  <Th>Status</Th>
                  <Th>When</Th>
                  <Th className="text-right">Opens</Th>
                  <Th className="w-24" />
                </tr>
              </thead>
              <tbody>
                {emails.map((email) => (
                  <Tr key={email.id}>
                    <Td onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${email.subject}`}
                        className="h-3.5 w-3.5 cursor-pointer accent-[rgb(var(--brand))]"
                        checked={selected.has(email.id)}
                        onChange={() =>
                          setSelected((current) => {
                            const next = new Set(current);
                            next.has(email.id) ? next.delete(email.id) : next.add(email.id);
                            return next;
                          })
                        }
                      />
                    </Td>
                    <Td className="cursor-pointer" onClick={() => setOpenId(email.id)}>
                      <button type="button" onClick={() => setOpenId(email.id)} className="rounded text-left font-medium text-ink hover:text-brand hover:underline">
                        {email.direction === 'INBOUND' ? '← ' : ''}
                        {truncate(email.subject, 56)}
                      </button>
                      {email.snippet ? (
                        <div className="text-xs text-faint">{truncate(email.snippet, 70)}</div>
                      ) : null}
                    </Td>
                    <Td className="cursor-pointer" onClick={() => setOpenId(email.id)}>
                      <div className="text-ink">{email.lead?.companyName ?? email.toEmail}</div>
                      <div className="text-xs text-faint">{truncate(email.toEmail, 32)}</div>
                    </Td>
                    <Td>
                      <EmailStatusBadge status={email.status} />
                      {email.error ? (
                        <div className="mt-0.5 text-2xs text-danger">{truncate(email.error, 40)}</div>
                      ) : null}
                    </Td>
                    <Td className="whitespace-nowrap text-faint">
                      {email.status === 'SCHEDULED' && email.scheduledAt ? (
                        <span className="flex items-center gap-1 text-warning">
                          <Clock className="h-3 w-3" />
                          {formatRelative(email.scheduledAt)}
                        </span>
                      ) : (
                        formatRelative(email.sentAt ?? email.receivedAt ?? email.createdAt)
                      )}
                    </Td>
                    <Td className="text-right tabular-nums text-muted">
                      {email.openCount || <span className="text-faint">—</span>}
                    </Td>
                    <Td>
                      <div className="flex justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setOpenId(email.id)}>
                          Open
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              page={data?.page ?? 1}
              totalPages={data?.totalPages ?? 1}
              total={data?.total ?? 0}
              pageSize={data?.pageSize ?? 25}
              onPage={(page) => setFilters((current) => ({ ...current, page }))}
            />
          </>
        )}
      </Card>

      <EmailDrawer emailId={openId} projectId={projectId} onClose={() => setOpenId(null)} />
    </Page>
  );
};

const EmailDrawer = ({
  emailId,
  projectId,
  onClose,
}: {
  emailId: string | null;
  projectId: string;
  onClose: () => void;
}) => {
  const { data: email } = useEmail(emailId ?? undefined);
  const emailAction = useEmailAction(projectId);
  const saveEmail = useSaveEmail(projectId);
  const deleteEmail = useDeleteEmail(projectId);
  const toast = useToast();

  const [draft, setDraft] = useState({ subject: '', body: '', scheduledAt: '' });
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!email) return;
    setDraft({
      subject: email.subject,
      body: email.bodyText,
      scheduledAt: email.scheduledAt ? email.scheduledAt.slice(0, 16) : '',
    });
  }, [email]);

  const editable = email
    ? ['DRAFT', 'SCHEDULED', 'FAILED', 'CANCELLED'].includes(email.status)
    : false;
  const dirty = Boolean(email) && (draft.subject !== email?.subject || draft.body !== email?.bodyText);

  const act = async (action: 'approve' | 'send' | 'cancel', message: string) => {
    if (!email) return;
    try {
      const body =
        action === 'approve' && draft.scheduledAt
          ? { scheduledAt: new Date(draft.scheduledAt).toISOString() }
          : undefined;
      const result = (await emailAction.mutateAsync({ id: email.id, action, body })) as unknown as {
        status?: string;
        reason?: string;
        dryRun?: boolean;
      };
      if (action === 'send' && result?.status === 'CANCELLED') {
        toast.error(`Not sent: ${result.reason}`);
        return;
      }
      toast.success(result?.dryRun ? `${message} (dry run — nothing left the machine)` : message);
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <>
      <Drawer
        open={Boolean(emailId)}
        onClose={onClose}
        width="lg"
        title={email ? truncate(email.subject, 64) : 'Email'}
        subtitle={
          email ? (
            <span className="flex flex-wrap items-center gap-2">
              <EmailStatusBadge status={email.status} />
              {email.campaign ? <Badge tone="brand">{email.campaign.name}</Badge> : null}
              <span className="text-2xs text-faint">
                {email.direction === 'INBOUND' ? 'from' : 'to'} {email.toEmail}
              </span>
            </span>
          ) : null
        }
        footer={
          email ? (
            <>
              <Button
                variant="ghost"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setConfirmDelete(true)}
                disabled={email.status === 'SENT'}
              >
                Delete
              </Button>
              <div className="flex-1" />
              {editable && dirty ? (
                <Button
                  icon={<Save className="h-4 w-4" />}
                  loading={saveEmail.isPending}
                  onClick={async () => {
                    try {
                      await saveEmail.mutateAsync({
                        id: email.id,
                        subject: draft.subject,
                        body: draft.body,
                      });
                      toast.success('Email updated');
                    } catch (error) {
                      toast.error(error);
                    }
                  }}
                >
                  Save edits
                </Button>
              ) : null}
              {email.status === 'DRAFT' ? (
                <Button
                  variant="primary"
                  icon={<Check className="h-4 w-4" />}
                  loading={emailAction.isPending}
                  onClick={() => act('approve', 'Approved and scheduled')}
                >
                  Approve
                </Button>
              ) : null}
              {editable ? (
                <Button
                  variant="primary"
                  icon={<Send className="h-4 w-4" />}
                  loading={emailAction.isPending}
                  onClick={() => act('send', 'Sent')}
                >
                  Send now
                </Button>
              ) : null}
              {email.status === 'SCHEDULED' ? (
                <Button icon={<Ban className="h-4 w-4" />} onClick={() => act('cancel', 'Cancelled')}>
                  Cancel
                </Button>
              ) : null}
            </>
          ) : null
        }
      >
        {!email ? (
          <div className="p-5 text-[13px] text-faint">Loading…</div>
        ) : (
          <div className="divide-y divide-line">
            <div className="space-y-4 px-5 py-4">
              <Field label="Subject" htmlFor="email-subject">
                <Input
                  id="email-subject"
                  value={draft.subject}
                  disabled={!editable}
                  onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
                />
              </Field>
              <Field
                label="Body"
                htmlFor="email-body"
                hint={
                  editable
                    ? 'Plain text. The signature and unsubscribe line are added when it is sent.'
                    : 'This message has been sent and can no longer be edited.'
                }
              >
                <Textarea
                  id="email-body"
                  rows={16}
                  className="prompt-input"
                  value={draft.body}
                  disabled={!editable}
                  onChange={(event) => setDraft({ ...draft, body: event.target.value })}
                />
              </Field>
              {email.status === 'DRAFT' || email.status === 'SCHEDULED' ? (
                <Field
                  label="Send at"
                  htmlFor="email-schedule"
                  hint="Leave empty to send at the next opportunity inside the mailbox window."
                >
                  <Input
                    id="email-schedule"
                    type="datetime-local"
                    value={draft.scheduledAt}
                    onChange={(event) => setDraft({ ...draft, scheduledAt: event.target.value })}
                  />
                </Field>
              ) : null}
            </div>

            <div className="px-5 py-4">
              <span className="field-label">Details</span>
              <dl className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
                {[
                  ['From', `${email.fromName ?? ''} <${email.fromEmail}>`],
                  ['To', email.toEmail],
                  ['Created', formatDateTime(email.createdAt)],
                  ['Scheduled', formatDateTime(email.scheduledAt)],
                  ['Sent', formatDateTime(email.sentAt)],
                  ['Sequence step', String(email.sequenceStep)],
                  ['Opens', formatNumber(email.openCount)],
                  ['Clicks', formatNumber(email.clickCount)],
                  ['Attempts', String(email.attempts)],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-2">
                    <dt className="shrink-0 text-faint">{label}</dt>
                    <dd className="truncate text-right text-ink">{value}</dd>
                  </div>
                ))}
              </dl>
              {email.error ? (
                <p className="mt-3 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">
                  {email.error}
                </p>
              ) : null}
            </div>

            <div className="px-5 py-4">
              <span className="field-label">Preview</span>
              <div
                className="rounded-lg border border-line bg-white p-4 text-[13px] text-slate-900"
                // The HTML here is generated by our own renderer from agent-authored plain
                // text, so there is no third-party markup to worry about.
                dangerouslySetInnerHTML={{ __html: email.bodyHtml }}
              />
            </div>
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete email"
        destructive
        confirmLabel="Delete"
        loading={deleteEmail.isPending}
        message="Delete this email? Only unsent messages can be removed."
        onConfirm={async () => {
          if (!email) return;
          try {
            await deleteEmail.mutateAsync(email.id);
            toast.success('Email deleted');
            setConfirmDelete(false);
            onClose();
          } catch (error) {
            toast.error(error);
          }
        }}
      />
    </>
  );
};

/** Exported for the inbox page, which reuses the same drawer for inbound mail. */
export { EmailDrawer };
